import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Generic content editor. Supports:
 *  - title (text input)
 *  - body (rich text editor)
 *  - optional CTA text (short input, shown when showCta=true)
 *  - optional image URL (shown when showImage=true)
 *
 * Can be used two ways:
 *  1. As a page with a `contentKey` prop (or from URL param)
 *  2. From /admin/icerik/:key route which reads config from SITE_SECTIONS
 */
export default function ContentEditor({
  contentKey,
  pageHeading,
  description,
  icon: Icon,
  redirectPath = "/admin/ayarlar",
  successMessage = "Kaydedildi",
  showCta = false,
  showImage = false,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const key = contentKey || params.key;

  const [form, setForm] = useState({ title: "", body: "", cta_text: "", image_url: "" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null); // {heading, description, showCta, showImage}

  useEffect(() => {
    if (!key) return;
    api.get(`/content/${key}`).then(({ data }) => {
      setForm({
        title: data.title || "",
        body: data.body || "",
        cta_text: data.cta_text || "",
        image_url: data.image_url || "",
      });
      // Auto-detect capabilities from stored fields
      setMeta({
        heading: pageHeading || `İçeriği düzenle: ${key}`,
        description,
        showCta: showCta || data.cta_text !== undefined,
        showImage: showImage || data.image_url !== undefined,
      });
      setLoaded(true);
    });
  }, [key, pageHeading, description, showCta, showImage]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim() || form.body === "<br>") {
      setError("Gövde metni boş olamaz.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = { title: form.title, body: form.body };
      if (meta?.showCta) payload.cta_text = form.cta_text;
      if (meta?.showImage) payload.image_url = form.image_url;
      await api.patch(`/content/${key}`, payload);
      toast.success(successMessage);
      navigate(redirectPath);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div className="k-container py-16">
        <div className="k-card h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-3">
          {Icon && (
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-sage-500/15 text-sage-700">
              <Icon className="h-6 w-6" />
            </span>
          )}
          <div>
            <h1 className="font-display text-5xl text-ink-900" data-testid={`content-editor-${key}-title`}>
              {meta.heading}
            </h1>
            {meta.description && <p className="font-body text-ink-700">{meta.description}</p>}
          </div>
        </div>

        <form onSubmit={submit} className="k-card mt-8 space-y-5 p-8" data-testid={`content-editor-${key}-form`}>
          <div>
            <label className="k-label">Başlık</label>
            <input
              className="k-input"
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid={`content-editor-${key}-title-input`}
            />
          </div>
          <div>
            <label className="k-label">Gövde</label>
            <RichTextEditor value={form.body} onChange={(html) => setForm((f) => ({ ...f, body: html }))} />
          </div>
          {meta.showCta && (
            <div>
              <label className="k-label">Buton metni (opsiyonel)</label>
              <input
                className="k-input"
                maxLength={60}
                value={form.cta_text}
                onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                placeholder="Ör: Kategorileri gör"
                data-testid={`content-editor-${key}-cta-input`}
              />
            </div>
          )}
          {meta.showImage && (
            <div>
              <label className="k-label">Görsel URL (opsiyonel)</label>
              <input
                className="k-input"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://…"
                data-testid={`content-editor-${key}-image-input`}
              />
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="Önizleme"
                  className="mt-3 h-40 w-full rounded-xl object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="k-btn-primary disabled:opacity-60" data-testid={`content-editor-${key}-submit`}>
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
            <button type="button" onClick={() => navigate(redirectPath)} className="k-btn-ghost">
              Vazgeç
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
